import { useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { QUOTE, CTA } from '@/constants/testIds';
import { SERVICES } from '@/data/site';
import { submitLead } from '@/lib/api';
import { CheckCircle2 } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

export default function QuoteForm({ source = 'contact-page' }) {
  const [state, setState] = useState({ name: '', phone: '', email: '', service: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setState((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!state.name || !state.phone || !state.email) {
      toast.error('Please add your name, phone and email.');
      return;
    }
    try {
      setLoading(true);
      await submitLead({ ...state, source });
      setSuccess(true);
      toast.success('Estimate request sent. We will call within one business day.');
      setState({ name: '', phone: '', email: '', service: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative border border-[#0A0A0A]/10 bg-white p-8 md:p-12 shadow-[0_1px_0_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(29,78,216,0.25)]">
      <div className="absolute -top-3 left-8 bg-[#DC2626] px-3 py-1 font-mono-label text-[10px] tracking-[0.24em] uppercase text-white">
        Free · No obligation
      </div>
      <div className="flex items-center justify-between mb-8">
        <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#1D4ED8]">// Request a free estimate</div>
        <div className="font-mono-label text-[10px] tracking-[0.24em] uppercase text-[#0A0A0A]/50">Response in 24h</div>
      </div>

      <AnimatePresence mode="wait">
        {success ? (
          <motion.div
            key="ok"
            data-testid={QUOTE.success}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="py-10"
          >
            <CheckCircle2 className="w-10 h-10 text-[#1D4ED8] mb-6" />
            <h3 className="font-display uppercase text-[#0A0A0A] text-3xl md:text-4xl leading-tight tracking-tight mb-4">
              Estimate request received.
            </h3>
            <p className="text-[#0A0A0A]/70 max-w-md">
              A project manager will call you within one business day to schedule a site visit. Thanks for trusting Alpha Tex.
            </p>
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="mt-8 font-mono-label text-[11px] tracking-[0.22em] uppercase text-[#0A0A0A]/80 border border-[#0A0A0A]/15 px-5 py-3 hover:bg-[#0A0A0A]/5"
            >
              Send another →
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid={QUOTE.form}
            onSubmit={onSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid md:grid-cols-2 gap-5"
          >
            <Field label="Your name" required>
              <input data-testid={QUOTE.name} value={state.name} onChange={onChange('name')} placeholder="Jane Doe" className="qf-input" required />
            </Field>
            <Field label="Phone" required>
              <input data-testid={QUOTE.phone} value={state.phone} onChange={onChange('phone')} placeholder="210-000-0000" className="qf-input" required />
            </Field>
            <Field label="Email" required>
              <input data-testid={QUOTE.email} type="email" value={state.email} onChange={onChange('email')} placeholder="you@email.com" className="qf-input" required />
            </Field>
            <Field label="Service">
              <select data-testid={QUOTE.service} value={state.service} onChange={onChange('service')} className="qf-input appearance-none">
                <option value="">Choose a service…</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
                <option value="Other">Something else</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Tell us about the project">
                <textarea data-testid={QUOTE.message} value={state.message} onChange={onChange('message')} rows={4} placeholder="Square footage, timeline, budget range, links to inspiration…" className="qf-input resize-none" />
              </Field>
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mt-2">
              <p className="text-[#0A0A0A]/55 font-mono-label text-[10px] tracking-[0.2em] uppercase max-w-xs">
                No spam. No sales calls. Just a scoped estimate.
              </p>
              <button
                type="submit"
                data-testid={QUOTE.submit || CTA.estimateBtn('quote-form')}
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-[#1D4ED8] text-white px-7 py-4 font-mono-label text-xs tracking-[0.24em] uppercase hover:bg-[#DC2626] transition-colors disabled:opacity-60"
              >
                {loading ? 'Sending…' : 'Send estimate request'}
                <span className="w-6 h-px bg-white group-hover:w-10 transition-[width] duration-500" />
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <style>{`
        .qf-input { width:100%; background:transparent; border:0; border-bottom:1px solid rgba(10,10,10,0.15); color:#0A0A0A; padding:14px 0; font-family:'Manrope',sans-serif; font-size:15px; outline:none; transition:border-color .3s; }
        .qf-input:focus { border-bottom-color:#1D4ED8; }
        .qf-input::placeholder { color: rgba(10,10,10,0.35); }
        select.qf-input option { background:#ffffff; color:#0A0A0A; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono-label text-[10px] tracking-[0.24em] uppercase text-[#0A0A0A]/55 mb-1">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </span>
      {children}
    </label>
  );
}
